import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import axios from 'axios';
import AuthorProfileSnippet from './Author/AuthorProfileSnippet';
import AuthorProfileModal from './Author/AuthorProfileModal';
import AboutAuthorModal from './AboutAuthorModal';
import { User, Headphones, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useReaderCredits } from '../contexts/ReaderCreditContext';
import { useReaderFunnel } from '../contexts/ReaderFunnelContext';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

// Cache for book description audio URLs (persists across modal sessions)
const audioCache = new Map();

const AboutBookModal = ({ book, isOpen, onClose, onAddToLibrary, onReadNow, isAuthenticated = false, onRegister }) => {
  const navigate = useNavigate();
  const [isReadingAloud, setIsReadingAloud] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [showManagedAuthorModal, setShowManagedAuthorModal] = useState(false);
  const [cachedAudioUrl, setCachedAudioUrl] = useState(null);
  const audioRef = useRef(null);
  const [isCoverZoomed, setIsCoverZoomed] = useState(false);
  const coverTriggerRef = useRef(null);
  const zoomCloseBtnRef = useRef(null);
  
  // State for displaying a different book selected from author's other titles
  const [displayedBook, setDisplayedBook] = useState(null);
  
  // Credit gating hooks
  const { balance } = useReaderCredits();
  const { setShowCreditExpiredModal, isBetaTester } = useReaderFunnel();
  
  // The book to actually display - either the override or the original prop
  const activeBook = displayedBook || book;
  
  // Reset displayedBook when the modal closes or the original book changes
  useEffect(() => {
    if (!isOpen) {
      setDisplayedBook(null);
    }
  }, [isOpen]);
  
  useEffect(() => {
    // When parent passes a new book, reset any override
    setDisplayedBook(null);
  }, [book?.id]);

  // Handler for when a book is selected from About Author modal
  const handleSelectBookFromAuthor = (selectedBook) => {
    console.log('[AboutBookModal] Book selected from author titles:', selectedBook.title);
    // Close the author modal
    setShowManagedAuthorModal(false);
    setShowAuthorModal(false);
    // Switch to the selected book
    setDisplayedBook(selectedBook);
    // Reset TTS state for new book
    setCachedAudioUrl(null);
    setIsReadingAloud(false);
  };

  // Check for cached audio when activeBook changes
  useEffect(() => {
    if (activeBook?.id && audioCache.has(activeBook.id)) {
      setCachedAudioUrl(audioCache.get(activeBook.id));
    } else {
      setCachedAudioUrl(null);
    }
  }, [activeBook?.id]);

  // Stop audio when modal closes or loses focus
  useEffect(() => {
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsReadingAloud(false);
    }
  }, [isOpen]);

  // Stop audio when author modal opens (modal loses focus)
  useEffect(() => {
    if (showAuthorModal && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsReadingAloud(false);
    }
  }, [showAuthorModal]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Cover zoom: close on Escape + return focus to trigger on close
  useEffect(() => {
    if (!isCoverZoomed) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation(); // prevent Dialog from also closing
        setIsCoverZoomed(false);
      }
    };
    // Focus the close button when the zoom opens (basic focus mgmt)
    zoomCloseBtnRef.current?.focus();
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      // Return focus to the cover trigger when zoom closes
      coverTriggerRef.current?.focus();
    };
  }, [isCoverZoomed]);

  // Auto-close zoom if the parent dialog closes
  useEffect(() => {
    if (!isOpen) setIsCoverZoomed(false);
  }, [isOpen]);

  const getSummary = (book) => {
    const description = book?.summary || book?.short_description || book?.description || '';
    if (!description) return 'No summary available yet for this title.';
    return description;
  };

  const handleReadAloud = async () => {
    if (!activeBook) return;

    // If already playing, pause
    if (isReadingAloud && audioRef.current) {
      audioRef.current.pause();
      setIsReadingAloud(false);
      return;
    }

    const textToRead = getSummary(activeBook);
    if (!textToRead || textToRead === 'No summary available yet for this title.') {
      toast.warning('No summary available to read aloud');
      return;
    }

    // Check if we have cached audio (in-memory or from previous session)
    if (cachedAudioUrl && audioRef.current) {
      console.log('[TTS] Playing cached audio for book:', activeBook.id);
      audioRef.current.src = cachedAudioUrl;
      audioRef.current.play();
      setIsReadingAloud(true);
      return;
    }

    // Fetch from backend (will use R2 cache if available)
    setTtsLoading(true);
    try {
      console.log('[TTS] Fetching audio for book:', activeBook.id);
      const response = await axios.post(`${API}/manuscripts/${activeBook.id}/tts/description`, 
        {},
        { responseType: 'blob' }
      );

      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Cache the audio URL for future use
      audioCache.set(activeBook.id, audioUrl);
      setCachedAudioUrl(audioUrl);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        setIsReadingAloud(true);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error('Failed to generate audio. Please try again.');
    } finally {
      setTtsLoading(false);
    }
  };

  const handleOpenAuthorModal = () => {
    // Debug: Log book object to see what author IDs are present
    console.log('[AboutBookModal] Opening author modal, book data:', {
      publisher_author_id: activeBook.publisher_author_id,
      managed_author_id: activeBook.managed_author_id,
      author_id: activeBook.author_id,
      owner_user_id: activeBook.owner_user_id,
      authorData: activeBook.authorData
    });
    
    // Check if this is a managed author (publisher) or regular author
    if (activeBook.publisher_author_id || activeBook.managed_author_id) {
      console.log('[AboutBookModal] Using managed author modal');
      setShowManagedAuthorModal(true);
    } else if (activeBook.authorData) {
      console.log('[AboutBookModal] Using regular author modal with authorData');
      setShowAuthorModal(true);
    } else {
      // Fallback - try to show managed author modal with author_id
      console.log('[AboutBookModal] Fallback: using managed author modal');
      setShowManagedAuthorModal(true);
    }
  };

  const handleClose = () => {
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsReadingAloud(false);
    }
    onClose();
  };

  // Handle guest sample playback - creates session and navigates to player
  const handlePlaySample = async () => {
    if (!activeBook?.id) return;
    
    try {
      // Create guest sample session
      const response = await axios.post(`${API}/manuscripts/${activeBook.id}/sample/guest`);
      const { session_id, anonymous_id } = response.data;
      
      console.log('[AboutBookModal] Guest sample session created:', session_id);
      
      // Navigate to player with guest sample mode flag
      navigate(`/player/${session_id}`, {
        state: {
          guestSample: true,
          anonymousId: anonymous_id,
          returnTo: '/public/library'
        }
      });
    } catch (error) {
      console.error('[AboutBookModal] Failed to start sample playback:', error);
      toast.error(error.response?.data?.detail || 'Failed to play sample. Please try again.');
    }
  };

  if (!activeBook) return null;

  return (
    <>
      <audio ref={audioRef} onEnded={() => setIsReadingAloud(false)} />
      
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[96vw] max-w-2xl h-[96vh] sm:h-auto sm:max-h-[90vh] overflow-y-auto p-3 sm:p-6 flex flex-col">
          <DialogHeader className="flex-shrink-0">
            {/* NewAudia Logo */}
            <div className="flex justify-center mb-2 sm:mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-8 sm:h-10 md:h-12 object-contain"
              />
            </div>
            
            <DialogTitle className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 text-center leading-tight">
              {activeBook.title}
            </DialogTitle>
            {activeBook.subtitle && (
              <DialogDescription className="text-sm sm:text-base md:text-lg text-gray-600 text-center mt-1">
                {activeBook.subtitle}
              </DialogDescription>
            )}
            {activeBook.seo_slug && (
              <a
                href={`/book/${activeBook.seo_slug}`}
                className="block text-center text-xs sm:text-sm text-indigo-600 hover:text-indigo-800 hover:underline mt-1 transition-colors"
                data-testid="about-book-view-page-link"
              >
                View book page
              </a>
            )}
          </DialogHeader>

          <div className="space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4 flex-1 overflow-y-auto">
            {/* Cover Image - with cache-busting + click-to-zoom */}
            <div className="flex justify-center py-1">
              <div
                ref={coverTriggerRef}
                role="button"
                tabIndex={0}
                aria-label="Zoom book cover"
                onClick={() => setIsCoverZoomed(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsCoverZoomed(true);
                  }
                }}
                className="relative inline-block cursor-zoom-in group focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 rounded-lg"
                data-testid="cover-zoom-trigger"
              >
                <img
                  src={activeBook.cover_image_path
                    ? `${BACKEND_URL}/api/covers/${activeBook.id}?v=${activeBook.cover_updated_at || activeBook.updated_at || Date.now()}`
                    : activeBook.cover_url || '/newaudia-logo.jpg'}
                  alt={activeBook.title}
                  className="w-28 h-40 sm:w-36 sm:h-52 md:w-48 md:h-72 object-cover rounded-lg shadow-lg pointer-events-none"
                />
                {/* Magnifying-glass affordance — always visible for mobile parity */}
                <span
                  aria-hidden="true"
                  className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-black/60 text-white shadow-md opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all pointer-events-none"
                >
                  <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
                </span>
              </div>
            </div>

            {/* Author with About Author button inline */}
            <div className="text-center space-y-1">
              <p className="text-sm sm:text-base md:text-lg text-gray-700">
                By <span className="font-semibold">{activeBook.author}</span>
              </p>
              {/* About the Author - Dark blue button below author name */}
              <button
                onClick={handleOpenAuthorModal}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] sm:text-xs font-bold text-white bg-blue-800 hover:bg-blue-700 rounded-full transition-colors"
              >
                <User className="w-3 h-3" />
                About the Author
              </button>
              
              {/* Play a Sample - Shown for unauthenticated users with samples */}
              {!isAuthenticated && activeBook.hasSample && (
                <button
                  onClick={() => {
                    handleClose();
                    handlePlaySample();
                  }}
                  className="inline-flex items-center gap-2 px-4 py-1.5 text-[10px] sm:text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-full transition-colors shadow-md"
                  data-testid="play-sample-btn"
                >
                  <Headphones className="w-3 h-3" />
                  Play a Sample
                  <Headphones className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Action Buttons - Moved up for visibility */}
            {!isAuthenticated ? (
              /* Unauthenticated User - Register Button */
              <div className="flex flex-col gap-2 pt-1 sm:pt-2">
                <Button
                  onClick={onRegister}
                  className="w-full sm:w-auto px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold text-xs sm:text-sm md:text-base"
                >
                  Register Now to Read Today
                </Button>
                {activeBook.purchase_url && (
                  <a
                    href={activeBook.purchase_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto"
                    data-testid="order-paperback-btn-guest"
                  >
                    <Button
                      variant="outline"
                      className="w-full px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base border-amber-600/50 text-amber-400 hover:bg-amber-600/10"
                    >
                      Order Paperback
                    </Button>
                  </a>
                )}
              </div>
            ) : (
              /* Authenticated User - Show Action Buttons */
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-1 sm:pt-2">
                <Button
                  onClick={() => {
                    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
                    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
                      console.log('[ABOUT BOOK MODAL] Read Now BLOCKED: No credits remaining (balance:', balance, ')');
                      toast.error('You need Reader Credits to listen. Please choose a plan to continue.', {
                        duration: 5000
                      });
                      setShowCreditExpiredModal(true);
                      return;
                    }
                    
                    if (onReadNow) {
                      onReadNow(activeBook.id);
                    }
                    handleClose();
                  }}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm md:text-base py-2 sm:py-2.5"
                  data-testid="read-now-btn"
                >
                  Read Now
                </Button>
                <Button
                  onClick={() => {
                    if (onAddToLibrary) {
                      onAddToLibrary(activeBook.id);
                    }
                    handleClose();
                  }}
                  variant="outline"
                  className="flex-1 text-xs sm:text-sm md:text-base py-2 sm:py-2.5"
                >
                  Add to Library
                </Button>
                {activeBook.purchase_url && (
                  <a
                    href={activeBook.purchase_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                    data-testid="order-paperback-btn"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-amber-600/50 text-amber-400 hover:bg-amber-600/10 text-xs sm:text-sm md:text-base py-2 sm:py-2.5"
                    >
                      Order Paperback
                    </Button>
                  </a>
                )}
              </div>
            )}

            {/* Metadata - Publisher */}
            {activeBook.publisher && (
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 text-[10px] sm:text-xs md:text-sm text-gray-600">
                <div>
                  <span className="font-semibold">Publisher:</span> {activeBook.publisher}
                </div>
              </div>
            )}

            {/* Summary with Read Aloud button inside */}
            <div className="bg-gradient-to-br from-purple-50 to-cyan-50 rounded-lg p-2.5 sm:p-3 md:p-4 border border-purple-200">
              <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                <h3 className="font-semibold text-gray-900 text-xs sm:text-sm md:text-base">About this book</h3>
                {/* Read Aloud - Small button inside the text container */}
                <button
                  onClick={handleReadAloud}
                  disabled={ttsLoading}
                  className="inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs text-purple-700 hover:text-purple-900 hover:bg-purple-100 rounded-full transition-colors border border-purple-300 hover:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Listen to the book description"
                >
                  {ttsLoading ? (
                    <>
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">Loading...</span>
                    </>
                  ) : isReadingAloud ? (
                    <>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                      </svg>
                      <span>Listen</span>
                    </>
                  )}
                </button>
              </div>
              <p className="text-gray-700 leading-relaxed text-xs sm:text-sm md:text-base">
                {getSummary(activeBook)}
              </p>
            </div>

            {/* About the Author Section - Legacy snippet view (only if authorData exists) */}
            {activeBook.authorData && (
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-2.5 sm:p-3 md:p-4 border border-slate-200">
                <h3 className="font-semibold text-gray-900 mb-2 text-xs sm:text-sm md:text-base">About the Author</h3>
                <AuthorProfileSnippet 
                  author={activeBook.authorData}
                  onViewFullProfile={() => setShowAuthorModal(true)}
                />
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-center pt-2 sm:pt-3 md:pt-4 flex-shrink-0">
              <Button
                onClick={handleClose}
                className="w-full sm:w-auto px-4 sm:px-6 md:px-8 text-xs sm:text-sm md:text-base py-2"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Author Profile Modal - For regular authors */}
      {activeBook.authorData && (
        <AuthorProfileModal
          isOpen={showAuthorModal}
          onClose={() => setShowAuthorModal(false)}
          author={activeBook.authorData}
        />
      )}

      {/* About Author Modal - For managed/publisher authors */}
      <AboutAuthorModal
        isOpen={showManagedAuthorModal}
        onClose={() => setShowManagedAuthorModal(false)}
        authorId={activeBook.publisher_author_id || activeBook.managed_author_id || activeBook.author_id || activeBook.owner_user_id}
        authorType={(activeBook.publisher_author_id || activeBook.managed_author_id) ? 'managed' : 'independent'}
        onSelectBook={handleSelectBookFromAuthor}
      />

      {/* Zoomed cover overlay — layered above the parent Dialog (z-50) */}
      {isCoverZoomed && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4 animate-in fade-in"
          onClick={() => setIsCoverZoomed(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Zoomed cover for ${activeBook.title}`}
          data-testid="cover-zoom-overlay"
        >
          <button
            ref={zoomCloseBtnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsCoverZoomed(false);
            }}
            aria-label="Close zoomed cover"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center justify-center w-10 h-10 rounded-full bg-black/50 text-white hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors"
            data-testid="cover-zoom-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <img
            src={activeBook.cover_image_path
              ? `${BACKEND_URL}/api/covers/${activeBook.id}?v=${activeBook.cover_updated_at || activeBook.updated_at || Date.now()}`
              : activeBook.cover_url || '/newaudia-logo.jpg'}
            alt={activeBook.title}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl cursor-default select-none"
            data-testid="cover-zoom-image"
          />
        </div>
      )}
    </>
  );
};

export default AboutBookModal;
